import type { PlanState } from '@aline/common';
import { PlanStateSchema } from '@aline/common';
import { Apollo } from 'src/gql/generated';

export type AssistantMessageVariant =
  | { planState: PlanState; variant: 'plan-tree' }
  | { planState: null; variant: 'error' | 'markdown' };

/**
 * Cache parsed PlanState by the raw subscription payload reference. The
 * Apollo cache reuses the same object identity until the subscription
 * fires again, so this folds N renders' worth of full superRefine walks
 * down to one parse per snapshot. WeakMap to avoid retaining old
 * snapshots beyond their cache lifetime.
 */
const parsedPlanStateCache = new WeakMap<object, PlanState | null>();

function parsePlanStateCached(planStateRaw: unknown): PlanState | null {
  if (planStateRaw == null || typeof planStateRaw !== 'object') return null;
  const cached = parsedPlanStateCache.get(planStateRaw);
  if (cached !== undefined) return cached;
  const parsed = PlanStateSchema.safeParse(planStateRaw);
  const result = parsed.success ? parsed.data : null;
  parsedPlanStateCache.set(planStateRaw, result);
  return result;
}

/**
 * Decides how to render an assistant message based on its status + raw
 * planState. PlanTree wins over the ERROR short-circuit: a failed harness
 * run with a valid planState should still render the per-node tree
 * (with the failed node highlighted natively) instead of dumping the
 * harness's accumulated text — which contains json-tool-call markers —
 * through V1 markdown. Pure (modulo the parse cache) so the routing
 * decision is unit-testable.
 */
export function pickAssistantMessageVariant(
  status: Apollo.AgentMessageStatus,
  planStateRaw: unknown
): AssistantMessageVariant {
  const planState = parsePlanStateCached(planStateRaw);
  if (planState) return { planState, variant: 'plan-tree' };
  if (status === Apollo.AgentMessageStatus.Error) {
    return { planState: null, variant: 'error' };
  }
  return { planState: null, variant: 'markdown' };
}
